'use client';

import { useState } from 'react';
import { Check, Pencil, Plus, X } from 'lucide-react';
import type { ClientField, MedicalDraft, MedicalItem, MedicalKind, MedicalType } from '@/lib/types';

/**
 * THE MEDICAL QUESTION, AS A MEDICAL QUESTION
 * ===========================================
 *
 * "Anything we should know?" over an empty box is how a peanut allergy ends
 * up as a sentence in a spreadsheet column, retyped by a parent every single
 * event and read by nobody until the night it matters. The profile widget
 * already files these properly — one row per allergy, condition, medication,
 * EpiPen, inhaler or device, with severity, an expiry and where the thing is
 * kept — so the registration form asks the same question the same way.
 *
 * WHAT IS ON FILE COMES FIRST. Most of this is already answered: the rows are
 * shown to confirm, not retyped. Adding is the same kind → what → severity →
 * kept-where sequence the profile page uses, so a family sees one medical
 * form at Woodside rather than one per app.
 *
 * AND CONFIRMING MEANS BEING ABLE TO SAY NO. A row on file was read-only, so a
 * misspelt allergy, a medication somebody stopped taking or an EpiPen two
 * years expired could only be contradicted in the free-text box underneath —
 * and the wrong row still turned up next year, and the year after. The person
 * looking at it is the one who knows. Editing and removing reach the real
 * `Contact_Medical_Items` row, the same one /profile edits, so the correction
 * outlives this registration. Removing end-dates rather than deletes: a
 * medication somebody came off is history, not a mistake.
 *
 * WHAT WE DID NOT COPY. The profile page's "card on file" — insurance,
 * physician, weight, over-the-counter consent — belongs to a week at camp,
 * not to three hours on a campus you drove to. And its empty state explains
 * itself at length; here the heading and the button are the whole of it.
 *
 * WHAT MP STORES is still the form field: everything here is summarised into
 * the answer, so the report the student team already runs keeps working.
 * Added rows ARE filed as real `Contact_Medical_Items` now, through
 * `onAddItem`, the same write /profile makes. The draft stays in the answer
 * either way, so a network error costs the row and never the information.
 */

const KIND_LABEL: Record<MedicalKind, string> = {
  allergy: 'Allergy',
  condition: 'Condition',
  medication: 'Medication',
  epipen: 'EpiPen',
  inhaler: 'Inhaler',
  device: 'Device',
};
const KINDS: MedicalKind[] = ['allergy', 'condition', 'medication', 'epipen', 'inhaler', 'device'];
/** The three that run out, and the only ones asked for a date. */
const EXPIRES: MedicalKind[] = ['epipen', 'inhaler', 'medication'];
const KEPT: { value: string; label: string }[] = [
  { value: 'with them', label: 'With them' },
  { value: 'nurse', label: 'With the nurse' },
  { value: 'leader', label: 'With a leader' },
  { value: 'front office', label: 'Front office' },
];

/** "Expires Mar 2027", and the fact it already has. */
function expiryNote(on: string): string {
  const when = new Date(`${on}T00:00:00`);
  const said = when.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  return when.getTime() < Date.now() ? `Expired ${said}` : `Expires ${said}`;
}

function line(m: MedicalItem | MedicalDraft): string {
  const bits = [
    `${KIND_LABEL[m.kind]}: ${m.label}`,
    m.severe ? 'severe' : null,
    m.detail,
    m.expiresOn ? expiryNote(m.expiresOn).toLowerCase() : null,
    m.keptWhere
      ? `kept ${KEPT.find((k) => k.value === m.keptWhere)?.label.toLowerCase() ?? m.keptWhere}`
      : null,
  ].filter(Boolean);
  return bits.join(' · ');
}

/**
 * What goes in the MP field: every item, then whatever they typed. One
 * answer, readable by a person with a clipboard at the door.
 */
export function medicalAnswer(onFile: MedicalItem[], added: MedicalDraft[], note: string): string {
  const all = [...onFile.map(line), ...added.map(line)];
  const said = all.join('\n');
  const extra = note.trim();
  if (!said) return extra;
  return extra ? `${said}\n${extra}` : said;
}

export function MedicalQuestion({
  field,
  onFile,
  added,
  setAdded,
  note,
  setNote,
  types,
  onSaveItem,
  onRemoveItem,
  onAddItem,
}: {
  field: ClientField;
  /** Their rows as MP already holds them. */
  onFile: MedicalItem[];
  added: MedicalDraft[];
  setAdded: (next: MedicalDraft[]) => void;
  note: string;
  setNote: (v: string) => void;
  types: MedicalType[];
  /**
   * Correct a row on file. Resolves false when the write failed, and the row
   * on screen goes back to what it was rather than lying about having changed.
   * Absent means the rows stay read-only, which is what a signed-out or
   * guest registrant gets.
   */
  onSaveItem?: (item: MedicalItem, patch: MedicalDraft) => Promise<boolean>;
  onRemoveItem?: (item: MedicalItem) => Promise<boolean>;
  /**
   * File a newly added row on the person's record as well as in the answer.
   * Resolves false when the write failed; the draft stays either way, so the
   * family has still told us even when it did not become a row.
   */
  onAddItem?: (draft: MedicalDraft) => Promise<boolean>;
}) {
  const [adding, setAdding] = useState(false);
  /** The row being corrected, by id. One at a time; this is not a spreadsheet. */
  const [editingId, setEditingId] = useState<number | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const canManage = Boolean(onSaveItem && onRemoveItem);
  const rows: (MedicalItem | MedicalDraft)[] = [...onFile, ...added];

  return (
    <section>
      <h3 className="text-navy text-[0.92rem] font-bold">{field.title ?? field.label}</h3>

      {rows.length > 0 && (
        <ul className="card divide-mist mt-2 divide-y overflow-hidden">
          {rows.map((m, i) => {
            const mine = i >= onFile.length;
            const filed = mine ? null : (m as MedicalItem);
            const busy = filed ? busyId === filed.id : false;

            // Correcting a row replaces it in place rather than opening a
            // second card below it: this is the same thing, said better.
            if (filed && editingId === filed.id) {
              return (
                <li key={`edit-${filed.id}`} className="p-2">
                  <ItemForm
                    types={types}
                    initial={filed}
                    submitLabel="Save"
                    onSubmit={async (patch) => {
                      setFailed(null);
                      setBusyId(filed.id);
                      const ok = await onSaveItem!(filed, patch);
                      setBusyId(null);
                      if (ok) setEditingId(null);
                      else setFailed('That change did not save. Try again in a minute.');
                    }}
                    onCancel={() => setEditingId(null)}
                  />
                </li>
              );
            }

            return (
              <li key={`${m.label}-${i}`} className="flex items-start gap-3 px-4 py-3">
                <span className={`min-w-0 flex-1 ${busy ? 'opacity-50' : ''}`}>
                  <span className="text-navy block text-sm font-bold">{m.label}</span>
                  <span className="text-slate block text-xs">
                    {KIND_LABEL[m.kind]}
                    {m.severe && <span className="text-rose font-bold"> · Severe</span>}
                    {m.expiresOn && ` · ${expiryNote(m.expiresOn)}`}
                    {m.keptWhere &&
                      ` · ${KEPT.find((k) => k.value === m.keptWhere)?.label ?? m.keptWhere}`}
                  </span>
                  {m.detail && (
                    <span className="text-slate-deep mt-0.5 block text-xs">{m.detail}</span>
                  )}
                </span>
                {mine ? (
                  <button
                    type="button"
                    aria-label={`Remove ${m.label}`}
                    onClick={() => setAdded(added.filter((_, j) => j !== i - onFile.length))}
                    className="text-slate hover:text-rose shrink-0 transition"
                  >
                    <X className="h-4 w-4" />
                  </button>
                ) : (
                  filed &&
                  canManage && (
                    <span className="flex shrink-0 items-center gap-2.5">
                      <button
                        type="button"
                        aria-label={`Edit ${filed.label}`}
                        disabled={busy}
                        onClick={() => {
                          setFailed(null);
                          setEditingId(filed.id);
                        }}
                        className="text-slate hover:text-teal-deep transition disabled:opacity-40"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        aria-label={`Remove ${filed.label}`}
                        disabled={busy}
                        onClick={async () => {
                          setFailed(null);
                          setBusyId(filed.id);
                          const ok = await onRemoveItem!(filed);
                          setBusyId(null);
                          if (!ok) setFailed('That did not come off. Try again in a minute.');
                        }}
                        className="text-slate hover:text-rose transition disabled:opacity-40"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </span>
                  )
                )}
              </li>
            );
          })}
        </ul>
      )}

      {failed && <p className="text-rose mt-2 text-xs font-semibold">{failed}</p>}

      {adding ? (
        <div className="mt-2">
          <ItemForm
            types={types}
            submitLabel="Add"
            onSubmit={(item) => {
              setAdded([...added, item]);
              setAdding(false);
              // The answer already has it. This is the second half: the same
              // row on the person's record, so next year's form shows it
              // instead of asking again.
              if (onAddItem) {
                setFailed(null);
                void onAddItem(item).then((ok) => {
                  if (!ok) setFailed('Saved to this registration, but not to their record.');
                });
              }
            }}
            onCancel={() => setAdding(false)}
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="border-line text-navy hover:border-teal mt-2 inline-flex items-center gap-1.5 rounded-full border border-dashed px-3.5 py-1.5 text-sm font-bold transition"
        >
          <Plus className="h-4 w-4" strokeWidth={3} />
          {rows.length ? 'Add something else' : 'Add an allergy or medication'}
        </button>
      )}

      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={3}
        maxLength={2000}
        placeholder="Anything else we should know"
        className="field mt-3"
      />
    </section>
  );
}

/**
 * One form, for adding a row and for correcting one.
 *
 * They were always the same questions — kind, what, severe, expiry, kept
 * where — so an edit that could not reach all of them would be a second,
 * worse version of this. `initial` is the row being corrected; without it the
 * form opens blank on an allergy, which is what most people are adding.
 */
function ItemForm({
  types,
  initial,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  types: MedicalType[];
  initial?: MedicalItem;
  submitLabel: string;
  onSubmit: (item: MedicalDraft) => void | Promise<void>;
  onCancel: () => void;
}) {
  const [kind, setKind] = useState<MedicalKind>(initial?.kind ?? 'allergy');
  const [typeId, setTypeId] = useState<number | null>(initial?.typeId ?? null);
  // A row typed in by hand has a label and no type id, and reopening it must
  // land back on "Something else" with the words still in the box.
  const [custom, setCustom] = useState(Boolean(initial && !initial.typeId));
  const [term, setTerm] = useState(initial && !initial.typeId ? initial.label : '');
  const [severe, setSevere] = useState(initial?.severe ?? false);
  const [expires, setExpires] = useState(initial?.expiresOn?.slice(0, 10) ?? '');
  const [kept, setKept] = useState<string | null>(initial?.keptWhere ?? null);
  const [detail, setDetail] = useState(initial?.detail ?? '');
  const [saving, setSaving] = useState(false);

  const options = types.filter((t) => t.kind === kind).sort((a, b) => a.sort - b.sort);
  // An EpiPen is an EpiPen. Asking what to call it is asking a question with
  // one answer.
  const fixed = kind === 'epipen' ? 'EpiPen' : kind === 'inhaler' ? 'Inhaler' : null;
  const label =
    fixed ??
    (custom || options.length === 0
      ? term.trim()
      : (options.find((t) => t.id === typeId)?.label ?? ''));

  const pickKind = (k: MedicalKind) => {
    setKind(k);
    setTypeId(null);
    setCustom(false);
    setTerm('');
  };

  return (
    <div className="border-line bg-mist/40 rounded-2xl border p-3">
      <div className="flex flex-wrap gap-1.5">
        {KINDS.map((k) => (
          <Chip key={k} on={kind === k} onClick={() => pickKind(k)}>
            {KIND_LABEL[k]}
          </Chip>
        ))}
      </div>

      {!fixed && (
        <div className="border-line mt-3 space-y-2 border-t border-dashed pt-3">
          {options.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {options.map((t) => (
                <Chip
                  key={t.id}
                  on={!custom && typeId === t.id}
                  onClick={() => {
                    setTypeId(t.id);
                    setCustom(false);
                  }}
                >
                  {t.label}
                </Chip>
              ))}
              <Chip
                on={custom}
                dashed
                onClick={() => {
                  setCustom(true);
                  setTypeId(null);
                }}
              >
                Something else
              </Chip>
            </div>
          )}
          {(custom || options.length === 0) && (
            <input
              autoFocus
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder={
                kind === 'medication'
                  ? 'Cetirizine'
                  : kind === 'allergy'
                    ? 'Peanuts'
                    : 'What is it?'
              }
              maxLength={100}
              className="field"
            />
          )}
        </div>
      )}

      {kind === 'medication' && (
        <input
          value={detail}
          onChange={(e) => setDetail(e.target.value)}
          placeholder="Dose and timing: 10 mg with breakfast"
          maxLength={200}
          className="field mt-2"
        />
      )}

      {EXPIRES.includes(kind) && (
        <label className="mt-3 block">
          <span className="kicker">Expires</span>
          <input
            type="date"
            value={expires}
            onChange={(e) => setExpires(e.target.value)}
            className="field mt-1 sm:max-w-xs"
          />
        </label>
      )}

      <div className="mt-3">
        <span className="kicker">Kept where</span>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {KEPT.map((k) => (
            <Chip
              key={k.value}
              on={kept === k.value}
              onClick={() => setKept(kept === k.value ? null : k.value)}
            >
              {k.label}
            </Chip>
          ))}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          aria-pressed={severe}
          onClick={() => setSevere((v) => !v)}
          className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-bold transition ${
            severe ? 'bg-rose-soft text-rose' : 'bg-paper border-line text-slate border'
          }`}
        >
          {severe && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
          Severe
        </button>
        <button
          type="button"
          disabled={!label || saving}
          onClick={async () => {
            setSaving(true);
            try {
              await onSubmit({
                kind,
                typeId: !custom && !fixed ? typeId : null,
                label,
                severe,
                detail: kind === 'medication' && detail.trim() ? detail.trim() : null,
                expiresOn: EXPIRES.includes(kind) && expires ? expires : null,
                keptWhere: kept,
              });
            } finally {
              setSaving(false);
            }
          }}
          className="btn btn-primary ml-auto disabled:opacity-50"
        >
          {saving ? 'Saving…' : submitLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-slate text-xs font-semibold hover:underline"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function Chip({
  on,
  dashed,
  onClick,
  children,
}: {
  on: boolean;
  dashed?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={`rounded-full px-3 py-1.5 text-xs font-bold transition ${
        on
          ? 'bg-teal border-teal border text-white'
          : `bg-paper text-slate-deep border-line hover:border-teal border ${dashed ? 'border-dashed' : ''}`
      }`}
    >
      {children}
    </button>
  );
}
